import React, { useState, useRef, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Upload, Crop, Save } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/components/Auth/AuthProvider';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

interface ProfileImageUploadProps {
  currentImage?: string;
  onImageUpdate: (imageUrl: string) => void;
  userInitials: string;
}

export const ProfileImageUpload = ({ 
  currentImage, 
  onImageUpdate, 
  userInitials 
}: ProfileImageUploadProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const [isUploading, setIsUploading] = useState(false);
  const [croppedSize, setCroppedSize] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const { user } = useAuth();
  const { toast } = useToast();

  const handleFileSelect = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast({
        title: "Invalid file type",
        description: "Please select an image file.",
        variant: "destructive",
      });
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast({
        title: "File too large",
        description: "Please select an image smaller than 2MB.",
        variant: "destructive",
      });
      return;
    }

    setSelectedFile(file);
    const reader = new FileReader();
    reader.onload = (e) => {
      setPreviewUrl(e.target?.result as string);
      setIsModalOpen(true);
    };
    reader.readAsDataURL(file);
  }, [toast]);

  const cropToCircle = useCallback(() => {
    if (!canvasRef.current || !previewUrl) return null;

    return new Promise<Blob>((resolve) => {
      const canvas = canvasRef.current!;
      const ctx = canvas.getContext('2d')!;
      const img = new Image();
      
      img.onload = () => {
        // Set canvas size to a square (50x50)
        const size = 50;
        canvas.width = size;
        canvas.height = size;
        
        // Clear canvas
        ctx.clearRect(0, 0, size, size);
        
        // Create circular clipping path
        ctx.beginPath();
        ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2);
        ctx.clip();
        
        // Calculate crop dimensions to maintain aspect ratio
        const imgSize = Math.min(img.width, img.height);
        const sourceX = (img.width - imgSize) / 2;
        const sourceY = (img.height - imgSize) / 2;
        
        // Draw the image
        ctx.drawImage(
          img,
          sourceX, sourceY, imgSize, imgSize, // source rectangle
          0, 0, size, size // destination rectangle
        );
        
        canvas.toBlob((blob) => {
          if (blob) setCroppedSize(blob.size);
          resolve(blob!);
        }, 'image/jpeg', 0.7);
      };
      
      img.src = previewUrl;
    });
  }, [previewUrl]);

  const handleUpload = async () => {
    if (!selectedFile || !user) return;

    setIsUploading(true);
    try {
      // Crop the image to circle
      const croppedBlob = await cropToCircle();
      if (!croppedBlob) throw new Error('Failed to crop image');

      // Enforce 5KB size limit
      if (croppedBlob.size > 5 * 1024) {
        toast({
          title: "Image too large",
          description: "Profile picture must be less than 5KB after cropping. Please use a smaller image.",
          variant: "destructive",
        });
        setIsUploading(false);
        return;
      }

      // For now, we'll convert to base64 and store it directly
      const reader = new FileReader();
      reader.onload = async (e) => {
        const imageDataUrl = e.target?.result as string;
        // Update user profile with the image
        const { error } = await supabase
          .from('user_profiles')
          .upsert({
            user_id: user.id,
            profile_picture: imageDataUrl,
            updated_at: new Date().toISOString()
          }, { onConflict: 'user_id' });
        if (error) throw error;
        onImageUpdate(imageDataUrl);
        setIsModalOpen(false);
        setSelectedFile(null);
        setPreviewUrl('');
        toast({
          title: "Profile picture updated",
          description: "Your profile picture has been updated successfully.",
        });
      };
      reader.readAsDataURL(croppedBlob);
    } catch (error) {
      console.error('Upload error:', error);
      toast({
        title: "Upload failed",
        description: "Failed to upload profile picture. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <>
      <div className="flex items-center gap-4">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div
                className="cursor-pointer"
                tabIndex={0}
                role="button"
                aria-label="Click to upload profile picture"
                onClick={() => fileInputRef.current?.click()}
                onKeyPress={e => (e.key === 'Enter' || e.key === ' ') && fileInputRef.current?.click()}
              >
                <Avatar className="h-20 w-20">
                  <AvatarImage src={currentImage} className="object-cover rounded-full" />
                  <AvatarFallback className="text-lg">
                    {userInitials}
                  </AvatarFallback>
                </Avatar>
              </div>
            </TooltipTrigger>
            <TooltipContent>
              Click to select a profile picture from your device
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        <div>
          <Button 
            variant="outline" 
            size="sm" 
            className="flex items-center gap-2"
            onClick={() => fileInputRef.current?.click()}
            aria-label="Upload Photo"
          >
            <Upload className="h-4 w-4" />
            Upload Photo
          </Button>
          <p className="text-sm text-gray-600 mt-1">JPG, PNG max 2MB. Click the avatar or button to select a file.</p>
        </div>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        className="hidden"
      />

      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Crop className="h-5 w-5" />
              Crop Profile Picture
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {previewUrl && (
              <div className="flex justify-center flex-col items-center gap-2">
                <div className="relative">
                  <img 
                    src={previewUrl} 
                    alt="Preview" 
                    className="max-w-full max-h-64 object-cover rounded-full border-4 border-green-200 shadow"
                  />
                  <div className="absolute inset-0 border-2 border-dashed border-gray-400 rounded-full opacity-50 pointer-events-none"></div>
                </div>
                {croppedSize !== null && (
                  <div className="text-xs text-gray-600 mt-1">
                    Cropped image size: {(croppedSize / 1024).toFixed(2)} KB (must be under 5KB)
                  </div>
                )}
                <div className="text-xs text-gray-500 mt-1">
                  After cropping, your image must be under 5KB. Try a simpler or smaller image if needed.
                </div>
              </div>
            )}
            
            <p className="text-sm text-gray-600 text-center">
              Your image will be cropped to a circular shape as shown above.
            </p>
            
            <div className="flex justify-end gap-2">
              <Button 
                variant="outline" 
                onClick={() => setIsModalOpen(false)}
                disabled={isUploading}
              >
                Cancel
              </Button>
              <Button 
                onClick={handleUpload}
                disabled={isUploading}
                className="flex items-center gap-2"
              >
                <Save className="h-4 w-4" />
                {isUploading ? 'Uploading...' : 'Save Picture'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Hidden canvas for image processing */}
      <canvas 
        ref={canvasRef} 
        className="hidden"
        width={50} 
        height={50}
      />
    </>
  );
};